// useHSPSNFTs.ts
// Custom hook to fetch House of Sports Store (HSPS) NFT collection
// from the Apertum Explorer API

import { useState, useEffect, useCallback } from 'react';
import { toPinataMediaUrl } from '../web3/ipfs';

// HSPS Collection Contract Address
const HSPS_CONTRACT_ADDRESS = '0x76841132Cb2281fD39135Ffc5Df7051BbA721449';
const API_BASE_URL = 'https://explorer.apertum.io/api/v2/tokens';

export interface HSPSNFTImage {
  id: string;
  url: string;
  alt: string;
  name: string;
  ipfsHash?: string;
}

interface NFTInstance {
  id: string;
  image_url: string | null;
  media_url: string | null;
  metadata: {
    name?: string;
    description?: string;
    image?: string;
  } | null;
}

interface APIResponse {
  items: NFTInstance[];
  next_page_params: {
    unique_token: string;
  } | null;
}

const getImageUrl = (nft: NFTInstance): string | null => {
  return (
    toPinataMediaUrl(nft.image_url) ||
    toPinataMediaUrl(nft.metadata?.image) ||
    toPinataMediaUrl(nft.media_url)
  );
};

interface UseHSPSNFTsOptions {
  maxItems?: number;
  shuffle?: boolean;
  autoRefresh?: boolean;
  refreshInterval?: number; // in milliseconds
}

interface UseHSPSNFTsReturn {
  nfts: HSPSNFTImage[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  isLoaded: boolean;
}

/**
 * Custom hook to fetch HSPS NFT images
 * 
 * @param options Configuration options
 * @returns NFT images, loading state, error state, and refetch function
 */
export const useHSPSNFTs = (options: UseHSPSNFTsOptions = {}): UseHSPSNFTsReturn => {
  const {
    maxItems = 20,
    shuffle = true,
    autoRefresh = false,
    refreshInterval = 60000, // 1 minute
  } = options;

  const [nfts, setNfts] = useState<HSPSNFTImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const fetchNFTs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = `${API_BASE_URL}/${HSPS_CONTRACT_ADDRESS}/instances`;
      
      const response = await fetch(apiUrl, {
        headers: {
          'accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const data: APIResponse = await response.json();

      if (!data.items || !Array.isArray(data.items)) {
        throw new Error('Invalid API response format');
      }

      // Transform API response to our image format
      let images: HSPSNFTImage[] = data.items
        .map((nft): HSPSNFTImage | null => {
          const imageUrl = getImageUrl(nft);
          if (!imageUrl) return null;

          const name = nft.metadata?.name || `HSPS #${nft.id}`;
          
          return {
            id: nft.id,
            url: imageUrl,
            alt: name,
            name: name,
            ipfsHash: undefined,
          };
        })
        .filter((img): img is HSPSNFTImage => img !== null);

      // Shuffle if requested
      if (shuffle) {
        images = images.sort(() => Math.random() - 0.5);
      }

      // Limit to maxItems
      images = images.slice(0, maxItems);

      setNfts(images);
      setIsLoaded(true);
    } catch (err) {
      console.error('Error fetching HSPS NFTs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch NFTs');
    } finally {
      setLoading(false);
    }
  }, [maxItems, shuffle]);

  // Initial fetch
  useEffect(() => {
    fetchNFTs();
  }, [fetchNFTs]);

  // Auto-refresh if enabled
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(fetchNFTs, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchNFTs]);

  return {
    nfts,
    loading,
    error,
    refetch: fetchNFTs,
    isLoaded,
  };
};

export default useHSPSNFTs;
